import { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { DBContext } from '@/lib/context';
import useUser from '@/lib/hooks/use-user';
import { ManuscriptCollectionService } from '@/data/manuscript/manuscript-collection-service';
import { useLocalStorage } from '@/lib/hooks/use-local-storage';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { BUILTIN_MARK_STYLES } from '@/data/manuscript/manuscript-constants';

interface ManuscriptsContextType {
    manuscripts: Manuscript[];
    loading: boolean;

    activeManuscriptId: string | null; // null = Reading Mode
    setActiveManuscriptId: (id: string | null) => void;

    activeManuscript: Manuscript | null;
    defaultManuscript: Manuscript | null;

    getManuscriptsForPassage: (passageId: string, bookName: string, version: string) => Manuscript[];

    ensureActiveManuscript: () => Promise<string>;
    createManuscript: (data: Partial<Manuscript>) => Promise<string>;
    updateManuscript: (id: string, updates: Partial<Manuscript>) => Promise<void>;
    deleteManuscript: (id: string) => Promise<void>;
}

const ManuscriptsContext = createContext<ManuscriptsContextType | undefined>(undefined);

export const DEFAULT_MANUSCRIPT_ID = 'personal-study';

export function ManuscriptsProvider({ children }: { children: ReactNode }) {
    const db = useContext(DBContext);
    const { user } = useUser();
    const [manuscripts, setManuscripts] = useState<Manuscript[]>([]);
    const [loading, setLoading] = useState(true);
    const { version } = usePassageState();

    // Persist active manuscript ID in localStorage
    const [activeManuscriptId, setActiveManuscriptId] = useLocalStorage<string | null>(
        'bg-active-manuscript',
        DEFAULT_MANUSCRIPT_ID
    );

    useEffect(() => {
        if (!user || !db) {
            setManuscripts([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const unsubscribe = ManuscriptCollectionService.subscribeToManuscripts(
            db,
            user.uid,
            (data) => {
                setManuscripts(data);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user, db]);

    const ghostDefault: Manuscript = {
        id: DEFAULT_MANUSCRIPT_ID,
        name: 'Personal Study',
        color: '#6366f1',
        scope: 'global',
        version: version,
        isDefault: true,
        sortOrder: -100,
        markStyles: BUILTIN_MARK_STYLES,
        dateCreated: 0,
        dateUpdated: 0
    };

    const activeManuscript = useMemo(() => {
        if (!activeManuscriptId) return null;
        const found = manuscripts.find(m => m.id === activeManuscriptId);
        if (found) return found;

        // If it's our stable ID and not created yet, show the ghost
        if (activeManuscriptId === DEFAULT_MANUSCRIPT_ID) return ghostDefault;

        return null;
    }, [activeManuscriptId, manuscripts, ghostDefault]);

    const defaultManuscript = useMemo(() => {
        return manuscripts.find(m => m.isDefault) || manuscripts[0] || ghostDefault;
    }, [manuscripts, ghostDefault]);

    const createManuscript = async (data: Partial<Manuscript>) => {
        if (!user || !db) throw new Error('Not authenticated');
        const id = await ManuscriptCollectionService.createManuscript(db, user.uid, data);
        setActiveManuscriptId(id);
        return id;
    };

    const updateManuscript = async (id: string, updates: Partial<Manuscript>) => {
        if (!user || !db) return;

        // If it's a ghost being materialized via edit, create it
        if (id === DEFAULT_MANUSCRIPT_ID && !manuscripts.some(m => m.id === id)) {
            await ManuscriptCollectionService.createManuscript(db, user.uid, {
                ...ghostDefault,
                ...updates,
                id: DEFAULT_MANUSCRIPT_ID
            });
            return;
        }

        await ManuscriptCollectionService.updateManuscript(db, user.uid, id, updates);
    };

    const deleteManuscript = async (id: string) => {
        if (!user || !db) return;

        // If we're deleting the active manuscript, switch to Reading Mode or default
        if (activeManuscriptId === id) {
            const next = manuscripts.find(m => m.id !== id);
            setActiveManuscriptId(next ? next.id : null);
        }

        await ManuscriptCollectionService.deleteManuscript(db, user.uid, id);
    };

    const getManuscriptsForPassage = useCallback((passageId: string, bookName: string, version: string) => {
        return manuscripts.filter(m => {
            if (m.version !== version) return false;

            switch (m.scope) {
                case 'global': return true;
                case 'book': return m.bookName === bookName;
                case 'passages': return m.passageIds?.includes(passageId) ?? false;
                default: return false;
            }
        });
    }, [manuscripts]);

    const value = {
        manuscripts,
        loading,
        activeManuscriptId,
        setActiveManuscriptId,
        activeManuscript,
        defaultManuscript,
        getManuscriptsForPassage,
        ensureActiveManuscript: async () => {
            // If we have a real active manuscript already, we're good
            if (activeManuscriptId && manuscripts.some(m => m.id === activeManuscriptId)) {
                return activeManuscriptId;
            }

            if (!user || !db) throw new Error('Not authenticated');

            // Use the stable personal-study ID for the first one
            const defaultM = await ManuscriptCollectionService.ensureDefaultManuscript(
                db,
                user.uid,
                version,
                DEFAULT_MANUSCRIPT_ID
            );

            setActiveManuscriptId(defaultM.id);
            return defaultM.id;
        },
        createManuscript,
        updateManuscript,
        deleteManuscript
    };

    return (
        <ManuscriptsContext.Provider value={value}>
            {children}
        </ManuscriptsContext.Provider>
    );
}

export function useManuscripts() {
    const context = useContext(ManuscriptsContext);
    if (context === undefined) {
        throw new Error('useManuscripts must be used within a ManuscriptsProvider');
    }
    return context;
}
